class AnniversaryCategoryTabs extends HTMLElement {
  connectedCallback() {
    this.tabs = Array.from(this.querySelectorAll('[data-anniversary-tab]'));
    this.status = this.querySelector('[data-anniversary-status]');
    if (this.tabs.length === 0) return;

    this.onClick = this.handleClick.bind(this);
    this.onKeydown = this.handleKeydown.bind(this);
    this.onCollectionReloaded = this.handleCollectionReloaded.bind(this);
    this.onPopstate = this.handlePopstate.bind(this);

    this.addEventListener('click', this.onClick);
    this.addEventListener('keydown', this.onKeydown);
    document.addEventListener('collection:reloaded', this.onCollectionReloaded);
    window.addEventListener('popstate', this.onPopstate);

    this.historyUrl = this.dataset.historyUrl || window.location.pathname;
    this.defaultCollectionHandle = this.dataset.defaultCollectionHandle || this.tabs[0]?.dataset.collectionHandle || '';
    this.templateView = this.dataset.templateView || '';

    const initialTab = this.syncFromUrl();
    this.syncPanelAccessibility();

    const currentCollectionHandle = this.dataset.currentCollectionHandle || this.defaultCollectionHandle;
    if (initialTab?.dataset.collectionHandle && initialTab.dataset.collectionHandle !== currentCollectionHandle) {
      this.renderTab(initialTab, window.location.search.slice(1), false);
    }
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('keydown', this.onKeydown);
    document.removeEventListener('collection:reloaded', this.onCollectionReloaded);
    window.removeEventListener('popstate', this.onPopstate);
    window.clearTimeout(this.loadingTimeout);
  }

  handleClick(event) {
    const tab = event.target.closest('[data-anniversary-tab]');
    if (!tab || !this.contains(tab) || tab.getAttribute('aria-disabled') === 'true') return;
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    event.preventDefault();
    if (tab.getAttribute('aria-selected') === 'true') return;
    this.selectTab(tab);
  }

  handleKeydown(event) {
    const currentTab = event.target.closest('[data-anniversary-tab]');
    if (!currentTab || !this.contains(currentTab)) return;

    const enabledTabs = this.tabs.filter((tab) => tab.getAttribute('aria-disabled') !== 'true');
    const currentIndex = enabledTabs.indexOf(currentTab);
    if (currentIndex === -1) return;
    let nextIndex = null;

    if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % enabledTabs.length;
    if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + enabledTabs.length) % enabledTabs.length;
    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = enabledTabs.length - 1;
    if (nextIndex === null) return;

    event.preventDefault();
    enabledTabs[nextIndex].focus();
  }

  selectTab(tab) {
    const facets = document.querySelector('rm-facets');
    if (!facets || typeof facets.render !== 'function') {
      this.setStatus('Product categories are temporarily unavailable. Please try again.');
      return;
    }

    const params = new URLSearchParams(window.location.search);
    Array.from(params.keys()).forEach((key) => {
      if (
        key.startsWith('filter.') ||
        key === 'page' ||
        key === 'constraint' ||
        key === 'rm_price' ||
        key === 'collection'
      ) {
        params.delete(key);
      }
    });

    const collectionHandle = tab.dataset.collectionHandle || '';
    if (collectionHandle && collectionHandle !== this.defaultCollectionHandle) {
      params.set('collection', collectionHandle);
    }

    this.setActiveTab(tab);
    this.renderTab(tab, params.toString(), true);
  }

  renderTab(tab, searchParams, updateHistory) {
    const facets = document.querySelector('rm-facets');
    if (!facets || typeof facets.render !== 'function') {
      this.setStatus('Product categories are temporarily unavailable. Please try again.');
      return;
    }

    const collectionUrl = tab.dataset.collectionUrl;
    if (!collectionUrl) return;

    this.classList.add('is-loading');
    this.syncPanelAccessibility(true);
    this.setStatus(`Loading ${tab.dataset.label || 'products'}…`);
    window.clearTimeout(this.loadingTimeout);
    this.loadingTimeout = window.setTimeout(() => {
      this.classList.remove('is-loading');
      this.syncPanelAccessibility(false);
    }, 20000);

    const collectionHandle = tab.dataset.collectionHandle || '';
    facets.render(searchParams, {
      updateHistory,
      resultsUrl: collectionUrl,
      historyUrl: this.historyUrl,
      collectionHandle: collectionHandle === this.defaultCollectionHandle ? '' : collectionHandle,
      resultView: this.templateView
    });
  }

  handleCollectionReloaded() {
    window.clearTimeout(this.loadingTimeout);
    this.classList.remove('is-loading');
    this.syncFromUrl();
    this.syncPanelAccessibility(false);

    const selectedTab = this.tabs.find((tab) => tab.getAttribute('aria-selected') === 'true');
    const resultCount = document.querySelector('.rm-facets__count')?.textContent?.trim();
    const label = selectedTab?.dataset.label || 'All';
    this.setStatus(resultCount ? `${label}: ${resultCount}` : `${label} products loaded.`);
  }

  syncFromUrl() {
    const params = new URLSearchParams(window.location.search);
    let collectionHandle = params.get('collection') || '';

    if (!collectionHandle && window.location.pathname !== this.historyUrl) {
      collectionHandle = this.dataset.currentCollectionHandle || '';
    }

    const matchedTab = this.tabs.find(
      (tab) =>
        tab.getAttribute('aria-disabled') !== 'true' &&
        tab.dataset.collectionHandle === (collectionHandle || this.defaultCollectionHandle)
    );

    if (collectionHandle && !matchedTab) {
      const url = new URL(window.location.href);
      url.searchParams.delete('collection');
      history.replaceState(history.state, '', `${this.historyUrl}${url.search}`);
    }

    const activeTab = matchedTab || this.tabs.find((tab) => tab.hasAttribute('data-default-tab')) || this.tabs[0];
    this.setActiveTab(activeTab);
    return activeTab;
  }

  handlePopstate() {
    const activeTab = this.syncFromUrl();
    if (activeTab) this.renderTab(activeTab, window.location.search.slice(1), false);
  }

  setActiveTab(activeTab) {
    this.tabs.forEach((tab) => {
      const selected = tab === activeTab;
      tab.setAttribute('aria-selected', String(selected));
      tab.tabIndex = selected ? 0 : -1;
    });
  }

  syncPanelAccessibility(isBusy = false) {
    const panel = document.getElementById('ProductGridContainer');
    const activeTab = this.tabs.find((tab) => tab.getAttribute('aria-selected') === 'true');
    if (!panel || !activeTab) return;

    panel.setAttribute('role', 'tabpanel');
    panel.setAttribute('aria-labelledby', activeTab.id);
    panel.setAttribute('aria-busy', String(isBusy));
  }

  setStatus(message) {
    if (this.status) this.status.textContent = message;
  }
}

if (!customElements.get('anniversary-category-tabs')) {
  customElements.define('anniversary-category-tabs', AnniversaryCategoryTabs);
}
